import React, { useState, useMemo } from 'react';
import { Task } from '../../../types';
import { KlSheet } from '../modal/KlSheet';
import { getTaskGanttRowNumber } from '../../../utils/planningUtils';

export interface MobileTaskSelectorSheetProps {
    open: boolean;
    onClose: () => void;
    tasks: Task[];
    onSelectTask: (task: Task, indexCode: string) => void;
    selectedTaskId?: string;
    currentFormItemIds?: string[];
}

export const MobileTaskSelectorSheet: React.FC<MobileTaskSelectorSheetProps> = ({
    open,
    onClose,
    tasks,
    onSelectTask,
    selectedTaskId,
    currentFormItemIds = []
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());

    const toggleCollapse = (id: string) => {
        setCollapsedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    // Helper para contar tareas hoja bajo una carpeta
    const countLeafTasks = (list: Task[]): number => {
        let count = 0;
        for (const t of list) {
            const isFolder = t.type === 'folder' || (t.children && t.children.length > 0);
            if (isFolder && t.children) {
                count += countLeafTasks(t.children);
            } else if (!isFolder) {
                count += 1;
            }
        }
        return count;
    };

    // Búsqueda plana si hay término de búsqueda
    const flatSearchResults = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return null;

        const results: { task: Task; code: string }[] = [];

        const traverse = (list: Task[]) => {
            list.forEach((t) => {
                const ganttNum = getTaskGanttRowNumber(t, tasks);
                const code = ganttNum !== null && ganttNum !== undefined ? `#${ganttNum}` : '-';
                const isFolder = t.type === 'folder' || (t.children && t.children.length > 0);
                if (isFolder && t.children) {
                    traverse(t.children);
                } else if (!isFolder) {
                    const matchName = (t.name || '').toLowerCase().includes(q);
                    const matchCode = code.toLowerCase().includes(q) || ((t as any).code || '').toLowerCase().includes(q);
                    if (matchName || matchCode) {
                        results.push({ task: t, code });
                    }
                }
            });
        };

        traverse(tasks);
        return results;
    }, [tasks, searchTerm]);

    // Sangría por nivel según maqueta 07: 12, 26, 40 y 54px. Desde el nivel 4 se queda en 54px.
    const getPaddingLeft = (lvl: number): number => {
        if (lvl <= 0) return 12;
        if (lvl === 1) return 26;
        if (lvl === 2) return 40;
        return 54;
    };

    // Render recursivo del árbol para navegación estándar (07-seleccionar-partida.html)
    const renderTree = (list: Task[], level = 0): React.ReactNode[] => {
        let elements: React.ReactNode[] = [];

        list.forEach((t) => {
            const ganttNum = getTaskGanttRowNumber(t, tasks);
            const currentCode = ganttNum !== null && ganttNum !== undefined ? `#${ganttNum}` : '-';
            const isFolder = t.type === 'folder' || (t.children && t.children.length > 0);
            const isCollapsed = collapsedIds.has(t.id);
            const paddingLeft = getPaddingLeft(level);

            if (isFolder) {
                const leafCount = t.children ? countLeafTasks(t.children) : 0;
                elements.push(
                    <div
                        key={t.id}
                        onClick={() => toggleCollapse(t.id)}
                        className="flex items-center gap-2 min-h-[44px] pr-3 border-b border-[#F1F5F9] bg-[#F8FAFC] dark:bg-slate-800/70 cursor-pointer select-none active:bg-slate-200/60 dark:active:bg-slate-700/60 transition-colors"
                        style={{ padding: `6px 12px 6px ${paddingLeft}px` }}
                    >
                        <svg
                            className={`w-4 h-4 text-[#64748B] transition-transform duration-150 shrink-0 ${isCollapsed ? '-rotate-90' : ''}`}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="m6 9 6 6 6-6" />
                        </svg>
                        <span className="font-mono text-[11px] font-semibold text-[#64748B] dark:text-slate-400 whitespace-nowrap">
                            {currentCode}
                        </span>
                        <span className="flex-1 text-[13px] leading-snug font-bold text-[#0F172A] dark:text-slate-100 truncate">
                            {t.name}
                        </span>
                        {leafCount > 0 && (
                            <span className="text-[11px] text-[#64748B] dark:text-slate-400 font-semibold shrink-0">
                                {leafCount}
                            </span>
                        )}
                    </div>
                );

                if (!isCollapsed && t.children) {
                    elements = [...elements, ...renderTree(t.children, level + 1)];
                }
            } else {
                // Tarea Hoja (Leaf Task) seleccionable
                const isSelected = selectedTaskId === t.id || currentFormItemIds.includes(t.id);
                // Si la partida hoja no tiene chevron, agrega margin-left: 24px al código para alinear con las carpetas
                const leafMarginLeft = level > 0 ? 24 : 0;

                elements.push(
                    <div
                        key={t.id}
                        onClick={() => {
                            onSelectTask(t, currentCode);
                            onClose();
                        }}
                        className={`flex items-center gap-2 min-h-[44px] pr-3 border-b border-[#F1F5F9] dark:border-slate-800 cursor-pointer select-none transition-colors active:scale-[0.99] ${
                            isSelected
                                ? 'bg-[#EFF6FF] dark:bg-blue-950/40 text-[#1D4ED8] dark:text-blue-300'
                                : 'bg-white dark:bg-slate-900 text-[#0F172A] dark:text-slate-100 active:bg-blue-50/50'
                        }`}
                        style={{ padding: `6px 12px 6px ${paddingLeft}px` }}
                    >
                        <span
                            className={`font-mono text-[11px] font-semibold whitespace-nowrap ${
                                isSelected ? 'text-[#1D4ED8] dark:text-blue-300' : 'text-[#64748B] dark:text-slate-400'
                            }`}
                            style={{ marginLeft: `${leafMarginLeft}px` }}
                        >
                            {currentCode}
                        </span>
                        <span className={`flex-1 text-[13px] leading-snug truncate ${isSelected ? 'font-bold' : 'font-medium'}`}>
                            {t.name}
                        </span>
                        {isSelected && (
                            <svg
                                className="w-[18px] h-[18px] text-[#2563EB] shrink-0"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M20 6 9 17l-5-5" />
                            </svg>
                        )}
                    </div>
                );
            }
        });

        return elements;
    };

    return (
        <KlSheet
            top={56}
            open={open}
            onClose={onClose}
            title="Seleccionar partida"
            footer={
                <button
                    type="button"
                    onClick={onClose}
                    className="w-full h-11 rounded-[12px] font-bold text-sm bg-white dark:bg-slate-800 border border-[#CBD5E1] dark:border-slate-700 text-[#334155] dark:text-slate-200 active:scale-[0.98] transition-transform"
                >
                    Cancelar
                </button>
            }
        >
            <div className="flex flex-col -mx-3.5 -mt-3.5">
                {/* Campo de búsqueda (07-seleccionar-partida.html) */}
                <div className="p-2.5 px-3 bg-white dark:bg-slate-900 border-b border-[#E2E8F0] dark:border-slate-800">
                    <div className="h-10 border border-[#E2E8F0] dark:border-slate-700 rounded-[10px] bg-white dark:bg-slate-800 px-3 flex items-center gap-2">
                        <svg
                            className="w-[18px] h-[18px] text-slate-400 shrink-0"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.3-4.3" />
                        </svg>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Buscar por código o nombre…"
                            className="w-full bg-transparent text-[13px] text-[#0F172A] dark:text-slate-100 placeholder:text-[#94A3B8] focus:outline-none"
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                onClick={() => setSearchTerm('')}
                                className="text-slate-400 hover:text-slate-600 p-1"
                            >
                                <span className="material-symbols-outlined text-[16px]">close</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* Contenedor de la jerarquía o resultados de búsqueda */}
                <div className="overflow-y-auto overscroll-contain pb-6">
                    {flatSearchResults !== null ? (
                        flatSearchResults.length === 0 ? (
                            <div className="p-8 text-center text-xs text-slate-400">
                                No se encontraron partidas coincidentes con "{searchTerm}".
                            </div>
                        ) : (
                            flatSearchResults.map(({ task, code }) => {
                                const isSelected = selectedTaskId === task.id || currentFormItemIds.includes(task.id);
                                return (
                                    <div
                                        key={task.id}
                                        onClick={() => {
                                            onSelectTask(task, code);
                                            onClose();
                                        }}
                                        className={`flex items-center gap-2 min-h-[44px] px-3 py-1.5 border-b border-[#F1F5F9] dark:border-slate-800 cursor-pointer select-none transition-colors ${
                                            isSelected
                                                ? 'bg-[#EFF6FF] dark:bg-blue-950/40 text-[#1D4ED8]'
                                                : 'bg-white dark:bg-slate-900 active:bg-blue-50/50'
                                        }`}
                                    >
                                        <span className={`font-mono text-[11px] font-semibold ${
                                            isSelected ? 'text-[#1D4ED8] dark:text-blue-300' : 'text-[#64748B] dark:text-slate-400'
                                        }`}>
                                            {code}
                                        </span>
                                        <span className={`flex-1 text-[13px] truncate ${isSelected ? 'font-bold' : 'font-medium'}`}>
                                            {task.name}
                                        </span>
                                        {isSelected && (
                                            <svg
                                                className="w-[18px] h-[18px] text-[#2563EB] shrink-0"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="M20 6 9 17l-5-5" />
                                            </svg>
                                        )}
                                    </div>
                                );
                            })
                        )
                    ) : (
                        renderTree(tasks)
                    )}
                </div>
            </div>
        </KlSheet>
    );
};
